import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Trash2, UserPlus, X } from 'lucide-react';
import { StudentLookup, StudentsApi } from '@/services/school.api';
import { LmsEnrollmentRow, LmsEnrollmentsApi } from '@/services/lms.api';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { formatDate } from '@/lib/format';
import { toast } from '@/stores/toast.store';

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Cap on the "already enrolled" cross-check fetch — a single bounded
 * request reusing the existing paginated list endpoint at a large page
 * size, not an unbounded load-all. For a course with more enrollments
 * than this, some already-enrolled students won't be pre-marked in the
 * picker; submitting is still safe either way — the bulk-enroll response's
 * `skipped` count is the actual source of truth, this is just a UX nicety. */
const ENROLLED_CROSSCHECK_CAP = 200;

export function LmsCourseEnrollmentsTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<LmsEnrollmentRow | null>(null);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['lms-enrollments', courseId, page, limit],
    queryFn: () => LmsEnrollmentsApi.list(courseId, { page, limit }),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (enrollmentId: string) => LmsEnrollmentsApi.remove(courseId, enrollmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-enrollments', courseId] });
      setConfirmRemove(null);
      toast.success('Student unenrolled');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Unenroll failed'),
  });

  const rows = pageData?.items ?? [];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">Students enrolled in this course.</p>
        <button className="btn-primary" onClick={() => setEnrollOpen(true)}>
          <UserPlus className="mr-1.5 h-4 w-4" /> Enroll students
        </button>
      </div>

      <DataTable<LmsEnrollmentRow>
        rows={rows}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No students enrolled yet."
        columns={[
          { key: 'admissionNumber', header: 'Adm #', render: (r) => r.admissionNumber ?? '—' },
          { key: 'studentName', header: 'Name', render: (r) => r.studentName ?? '—' },
          {
            key: 'class',
            header: 'Class · Section',
            render: (r) =>
              r.className ? `${r.className}${r.sectionName ? ` · ${r.sectionName}` : ''}` : '—',
          },
          { key: 'progress', header: 'Progress', render: (r) => `${r.progress}%` },
          { key: 'enrolledAt', header: 'Enrolled', render: (r) => formatDate(r.enrolledAt) },
        ]}
        actions={(r) => (
          <button
            className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => setConfirmRemove(r)}
            title="Unenroll"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      />

      {pageData && (
        <Pagination
          page={pageData.page}
          totalPages={pageData.totalPages}
          total={pageData.total}
          limit={pageData.limit}
          onPageChange={setPage}
          onLimitChange={(l) => {
            setPage(1);
            setLimit(l);
          }}
        />
      )}

      {enrollOpen && (
        <EnrollStudentsModal courseId={courseId} onClose={() => setEnrollOpen(false)} />
      )}

      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => confirmRemove && remove.mutate(confirmRemove.id)}
        loading={remove.isPending}
        title="Unenroll student?"
        message={`Remove "${confirmRemove?.studentName}" from this course. Their progress record is soft-deleted, not destroyed.`}
        confirmText="Unenroll"
      />
    </>
  );
}

function EnrollStudentsModal({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Map<string, StudentLookup>>(new Map());

  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ['students-lookup-lms', search],
    queryFn: () => StudentsApi.lookup({ search: search.trim() || undefined, status: 'active' }),
  });

  // Best-effort cross-check — see ENROLLED_CROSSCHECK_CAP's doc comment.
  const { data: enrolledPage } = useQuery({
    queryKey: ['lms-enrollments-crosscheck', courseId],
    queryFn: () => LmsEnrollmentsApi.list(courseId, { page: 1, limit: ENROLLED_CROSSCHECK_CAP }),
  });
  const enrolledIds = useMemo(
    () => new Set((enrolledPage?.items ?? []).map((e) => e.studentId)),
    [enrolledPage],
  );
  const crosscheckIncomplete = (enrolledPage?.total ?? 0) > ENROLLED_CROSSCHECK_CAP;

  const toggle = (s: StudentLookup) => {
    if (enrolledIds.has(s.id)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(s.id)) next.delete(s.id);
      else next.set(s.id, s);
      return next;
    });
  };

  const enroll = useMutation({
    mutationFn: () => LmsEnrollmentsApi.bulkEnroll(courseId, [...selected.keys()]),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['lms-enrollments', courseId] });
      const parts = [`${res.enrolled} enrolled`];
      if (res.skipped) parts.push(`${res.skipped} skipped (already enrolled)`);
      if (res.invalidIds.length) parts.push(`${res.invalidIds.length} invalid`);
      toast.success(parts.join(', '));
      onClose();
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Enroll failed'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Enroll students"
      description="Search by name or admission number."
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => enroll.mutate()}
            disabled={selected.size === 0 || enroll.isPending}
          >
            {enroll.isPending ? 'Enrolling…' : `Enroll ${selected.size || ''}`.trim()}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search students…"
        />

        {selected.size > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {[...selected.values()].map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-200"
              >
                {s.studentName}
                <button onClick={() => toggle(s)} className="hover:text-brand-900">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-slate-400">Searching…</div>
          ) : candidates.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-400">No students found.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {candidates.map((s) => {
                const alreadyEnrolled = enrolledIds.has(s.id);
                const isSelected = selected.has(s.id);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      disabled={alreadyEnrolled}
                      onClick={() => toggle(s)}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                        alreadyEnrolled
                          ? 'cursor-not-allowed bg-slate-50 text-slate-400'
                          : 'hover:bg-slate-50 text-slate-800'
                      }`}
                    >
                      <span>
                        <span className="font-medium">{s.studentName}</span>{' '}
                        <span className="text-slate-400">({s.admissionNumber})</span>
                      </span>
                      {alreadyEnrolled ? (
                        <Badge tone="slate">Already enrolled</Badge>
                      ) : isSelected ? (
                        <Check className="h-4 w-4 text-brand-600" />
                      ) : (
                        <Plus className="h-4 w-4 text-slate-300" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {crosscheckIncomplete && (
          <p className="text-xs text-slate-400">
            This course has many enrollments — some already-enrolled students may not show as
            disabled above. Submitting is still safe; duplicates are skipped automatically.
          </p>
        )}
      </div>
    </Modal>
  );
}
